import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  LogOut, Save, RotateCcw, Plus, Trash2, ArrowLeft, ShieldCheck, Lock,
  RefreshCw, Youtube, Trophy, Menu,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { loadState, saveState, resetState } from "../store";
import { useAuth } from "../auth";
import { UsersToolbar } from "../components/admin/UsersToolbar";
import { UserAccessDialog } from "../components/admin/UserAccessDialog";
import { BalanceDialog } from "../components/admin/BalanceDialog";
import { UsersList } from '../components/admin/UsersList';
import { AnalyticsDashboard } from '../components/admin/AnalyticsDashboard';
import { AdminStakePanel } from '../components/admin/AdminStakePanel';
import { KickletAdminWorkspace } from '../components/kicklet/KickletAdminWorkspace';
import { UserManagementDialog } from '../components/admin/UserManagementDialog';
import { UserControlDialog } from '../components/admin/UserControlDialog';
import { DeleteUserDialog } from '../components/admin/DeleteUserDialog';
import { AdminSectionCard as SectionCard, AdminBoardSelector } from '../components/admin/AdminSectionCard';
import { mergeSheetStandings } from "../components/admin/syncStandings";
import { AdminOriginalsGate } from "../components/admin/AdminOriginalsGate";
import { PasswordRevealDialog } from '../components/admin/PasswordRevealDialog';
import { AdminNavigation, adminSections } from '../components/admin/AdminNavigation';
import { useTheme } from '../theme';
import { useVideoFeed } from '../components/useVideoFeed';
import { YouTubeFeedStatus } from '../components/admin/YouTubeFeedStatus';
import { AdminToolsPanel } from '../components/admin/AdminToolsPanel';
import { adminLocation } from '../components/admin/adminRoutes';
import { AdminSidebarToggle } from '../components/admin/AdminSidebarToggle';
import { AdminLeaderboardSource, initialLeaderboardConfig, normalizeLeaderboardConfig, leaderboardFailure } from '../components/admin/AdminLeaderboardSource';
import { withoutCachedStandings, maskApiLeaderboard } from '../lib/leaderboardPrivacy';

const signupTime = (createdAt) => {
  const value = createdAt ? new Date(createdAt).getTime() : NaN;
  return Number.isFinite(value) ? value : null;
};

const Field = ({ label, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={props.id || props["data-testid"]} className="text-xs text-slate-400">{label}</Label>
    <Input id={props.id || props["data-testid"]} {...props} className="bg-white/5 border-white/10 text-white" />
  </div>
);

const AdminSection = ({ open, testid, children }) => open ? <section className="admin-active-section" data-testid={`${testid}-container`}>{children}</section> : null;

const Gate = () => {
  const { toast } = useToast();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) {
      toast({ title: "Access denied", description: res.error, variant: "destructive" });
    } else if (res.user.role !== "admin") {
      toast({ title: "Not authorized", description: "This account does not have admin access.", variant: "destructive" });
    }
  };
  return (
    <div className="admin-panel min-h-screen flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl glass p-8">
        <div className="flex items-center gap-2 mb-6">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/15 ring-1 ring-emerald-400/30">
            <Lock className="h-5 w-5 text-emerald-400" />
          </span>
          <div>
            <h1 className="font-display text-lg font-bold text-white">Admin Access</h1>
            <p className="text-xs text-slate-400">GVRewards control panel</p>
          </div>
        </div>
        <div className="space-y-4">
          <Field
            label="Username or email"
            data-testid="admin-login-email"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Username or email"
            autoFocus
          />
          <Field
            label="Password"
            data-testid="admin-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
          />
        </div>
        <Button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full mt-6 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold disabled:opacity-60">
          <ShieldCheck className="mr-2 h-4 w-4" /> {loading ? "Verifying…" : "Unlock"}
        </Button>
      </form>
    </div>
  );
};

const emptyBoard = () => ({
  settings: { prizePool: 0, affiliateCode: "" },
  topThree: [],
  leaderboard: [],
});

const Admin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const logo = theme !== 'venom' ? '/gv-logo-onyx.png' : '/gv-logo.png';
  const location = adminLocation(searchParams);
  const activeSection = adminSections.find((section) => section.id === location.section) || adminSections.find((section) => section.id === 'analytics');
  useEffect(() => {
    const resolved = adminLocation(searchParams);
    if (searchParams.get('section') !== resolved.section || searchParams.has('tab') || searchParams.get('view') !== resolved.view) {
      const params = new URLSearchParams(searchParams); params.set('section', resolved.section); params.delete('tab');
      if (resolved.view) params.set('view', resolved.view); else params.delete('view');
      navigate(`/admin?${params.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);
  const [navCollapsed, setNavCollapsed] = useState(false), [mobileNav, setMobileNav] = useState(false);
  const [originalsActionsTarget, setOriginalsActionsTarget] = useState(null);
  const { toast } = useToast();
  const { user, loading, logout: authLogout } = useAuth();
  const [data, setData] = useState(() => withoutCachedStandings(loadState()));
  const [editBoard, setEditBoard] = useState("stake"); // 'stake' | 'exclusive'


  const [sheetCfg, setSheetCfg] = useState(initialLeaderboardConfig);
  const [sheetReady, setSheetReady] = useState(false), [savedSource, setSavedSource] = useState('google_sheet');
  const [sheetError, setSheetError] = useState(''), [sheetStatusKind, setSheetStatusKind] = useState('Saved source results');
  const [sheetStatus, setSheetStatus] = useState(null);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [ytChannels, setYtChannels] = useState([""]);
  const [ytStatus, setYtStatus] = useState(null);
  const [ytBusy, setYtBusy] = useState(false);
  const [ytError, setYtError] = useState('');
  const { feed: ytFeed, error: ytFeedError } = useVideoFeed(user?.role === 'admin' && activeSection.id === 'youtube');
  useEffect(() => { if (ytFeed) setYtStatus(ytFeed); }, [ytFeed]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [balanceAccount, setBalanceAccount] = useState(null);
  const [accessAccount, setAccessAccount] = useState(null);
  const [controlAccount, setControlAccount] = useState(null);
  const [managedAccount, setManagedAccount] = useState(null);
  const [deleteAccount, setDeleteAccount] = useState(null);
  const [managedAccountTab, setManagedAccountTab] = useState('details');
  const [userSearch, setUserSearch] = useState("");
  useEffect(() => { if (searchParams.has('search')) setUserSearch(searchParams.get('search')); }, [searchParams]);
  const [userSort, setUserSort] = useState("newest");
  useEffect(() => {
    const accountId = searchParams.get('account');
    if (activeSection.id !== 'users' || !accountId || user?.role !== 'admin') return;
    const account = users.find((entry) => entry.id === accountId);
    if (!account) return;
    setManagedAccountTab('details'); setManagedAccount(account);
    const params = new URLSearchParams(searchParams); params.delete('account');
    navigate(`/admin?${params.toString()}`, { replace: true });
  }, [activeSection.id, searchParams, users, user?.role, navigate]);
  const visibleUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    return users.filter((account) => [account.email, account.username, account.stake_username, account.ip_address, account.discord_id, account.discord_username, account.discord_global_name]
      .some((value) => String(value || "").toLowerCase().includes(query)))
      .sort((a, b) => {
        const first = signupTime(a.created_at);
        const second = signupTime(b.created_at);
        // Undated legacy accounts stay at the bottom in either sort direction.
        if (first === null || second === null) return first === second ? 0 : first === null ? 1 : -1;
        return userSort === "oldest" ? first - second : second - first;
      });
  }, [users, userSearch, userSort]);
  const [revealAccount, setRevealAccount] = useState(null);
  useEffect(() => { setRevealAccount(null); }, [activeSection.id]);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("gv_token") || ""}`,
  });

  useEffect(() => {
    if (user && user.role === "admin") {
      fetch(`${API}/admin/leaderboard-config`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (!d) throw new Error(); setSheetCfg(normalizeLeaderboardConfig(d)); setSavedSource(d.source || 'google_sheet'); setSheetReady(true); })
        .catch(() => setSheetError('Unable to load leaderboard settings. Reload before making changes.'));
      fetch(`${API}/admin/youtube-config`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => d && setYtChannels(d.channels && d.channels.length ? d.channels : [""]))
        .catch(() => {});
      loadUsers();
      loadSheetStandings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const r = await fetch(`${API}/admin/users`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Unable to load users');
      const d = await r.json();
      setUsers(d.users || []);
    } catch {
      toast({ title: 'Unable to load users', description: 'Please try refreshing the list.', variant: 'destructive' });
    } finally { setUsersLoading(false); }
  };

  const syncSheetStandings = (sources) => {
    setData((current) => mergeSheetStandings(current, sources));
    // Cache only fetched boards, without accidentally saving other unsaved edits.
    saveState(mergeSheetStandings(loadState(), sources));
  };

  const loadSheetStandings = async () => {
    const results = await Promise.all(["stake", "exclusive"].map(async (boardName) => {
      try {
        const response = await fetch(`${API}/leaderboard/${boardName}`, { headers: { Authorization: `Bearer ${localStorage.getItem('gv_token')}` } });
        if (!response.ok) return [boardName, { ok: false }];
        const payload = await response.json();
        return [boardName, { ...payload, ok: payload.configured }];
      } catch { return [boardName, { ok: false }]; }
    }));
    syncSheetStandings(Object.fromEntries(results));
  };

  const saveYtCfg = async () => {
    setYtBusy(true);
    setYtError('');
    try {
      const channels = ytChannels.map((c) => c.trim()).filter(Boolean);
      const r = await fetch(`${API}/admin/youtube-config`, {
        method: "PUT", headers: authHeaders(), body: JSON.stringify({ channels }),
      });
      const saved = await r.json();
      if (!r.ok) throw new Error(typeof saved.detail === 'string' ? saved.detail : 'Could not save YouTube channels.');
      setYtChannels(saved.channels.length ? saved.channels : ['']);
      toast({ title: "YouTube channels saved", description: "Latest Videos will now show uploads from these channels." });
      await refreshYt();
    } catch (failure) {
      setYtError(failure.message);
      toast({ title: "Save failed", description: failure.message, variant: "destructive" });
    } finally {
      setYtBusy(false);
    }
  };

  const refreshYt = async () => {
    setYtBusy(true);
    setYtError('');
    try {
      const r = await fetch(`${API}/admin/youtube/refresh`, { method: "POST", headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not refresh YouTube channels.');
      setYtStatus(d);
      window.dispatchEvent(new Event('gv-videos-updated'));
    } catch (failure) {
      setYtError(failure.message);
      toast({ title: "Refresh failed", variant: "destructive" });
    } finally {
      setYtBusy(false);
    }
  };

  const saveSheetCfg = async () => {
    setSheetBusy(true);
    setSheetError('');
    try {
      const r = await fetch(`${API}/admin/leaderboard-config`, {
        method: "PUT", headers: authHeaders(), body: JSON.stringify(sheetCfg),
      });
      const saved = await r.json();
      if (!r.ok) throw new Error(leaderboardFailure(saved));
      setSheetCfg(normalizeLeaderboardConfig(saved)); setSavedSource(saved.source);
      toast({ title: "Leaderboard source saved", description: `Results now use ${saved.source === 'stake_api' ? 'Stake API' : 'Google Sheet'}.` });
      await refreshSheets();
    } catch (failure) {
      setSheetError(failure.message);
      toast({ title: "Save failed", description: failure.message, variant: "destructive" });
    } finally {
      setSheetBusy(false);
    }
  };

  const refreshSheets = async () => {
    setSheetBusy(true);
    setSheetError('');
    try {
      const r = await fetch(`${API}/admin/leaderboard/refresh`, { method: "POST", headers: authHeaders() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setSheetStatus(d);
      setSheetStatusKind('Saved source results');
      syncSheetStandings(d);
    } catch {
      toast({ title: "Refresh failed", variant: "destructive" });
    } finally {
      setSheetBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-panel min-h-screen flex items-center justify-center">
        <p data-testid="admin-loading" className="text-slate-400 text-sm">Loading…</p>
      </div>
    );
  }

  if (!user) return <Gate />;

  if (user.role !== "admin") {
    return (
      <div className="admin-panel min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl glass p-8 text-center">
          <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-rose-500/15 ring-1 ring-rose-400/30">
            <Lock className="h-6 w-6 text-rose-400" />
          </span>
          <h1 data-testid="admin-access-restricted" className="font-display text-lg font-bold text-white">Access Restricted</h1>
          <p className="text-sm text-slate-400 mt-1">Your account doesn't have admin permissions.</p>
          <div className="mt-6 flex gap-2 justify-center">
            <Button data-testid="admin-restricted-home" variant="outline" onClick={() => navigate("/")} className="border-white/15 text-slate-200 hover:bg-white/5">
              Go Home
            </Button>
            <Button data-testid="admin-switch-account" onClick={() => authLogout()} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              Switch Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Return the board object currently being edited
  const testLeaderboardSource = async () => {
    setSheetBusy(true); setSheetError('');
    try {
      const response = await fetch(`${API}/admin/leaderboard/test`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(sheetCfg) });
      const result = await response.json();
      if (!response.ok) throw new Error(leaderboardFailure(result));
      setSheetStatus({ stake: maskApiLeaderboard(result.stake), exclusive: maskApiLeaderboard(result.exclusive) }); setSheetStatusKind('Preview only — active source unchanged');
    } catch (failure) { setSheetError(failure.message); }
    finally { setSheetBusy(false); }
  };

  // Return the board object currently being edited
  const board =
    editBoard === "exclusive"
      ? data.exclusive || emptyBoard()
      : { settings: data.settings, topThree: data.topThree, leaderboard: data.leaderboard };

  // Write an updated board back into the correct location of the state
  const writeBoard = (updated) => {
    if (editBoard === "exclusive") {
      setData({ ...data, exclusive: updated });
    } else {
      setData({
        ...data,
        settings: updated.settings,
        topThree: updated.topThree,
        leaderboard: updated.leaderboard,
      });
    }
  };

  // Unified standings list (top 3 + the rest) sorted by rank.
  const maxRows = 20;
  const combined = [...(board.topThree || []), ...(board.leaderboard || [])]
    .slice()
    .sort((a, b) => (Number(a.rank) || 0) - (Number(b.rank) || 0));

  // Split a flat list back into topThree (rank<=3) and leaderboard (rank>3).
  const writeCombined = (list) => {
    const sorted = list.slice().sort((a, b) => (Number(a.rank) || 0) - (Number(b.rank) || 0));
    writeBoard({
      ...board,
      topThree: sorted.filter((r) => (Number(r.rank) || 0) <= 3),
      leaderboard: sorted.filter((r) => (Number(r.rank) || 0) > 3),
    });
  };

  const updatePlayer = (idx, key, value) => {
    writeCombined(combined.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
    if (sheetCfg.source === 'stake_api' && key === 'prize') {
      setSheetCfg((config) => ({ ...config, api_prizes: { ...config.api_prizes, [editBoard]: { ...Object.fromEntries(combined.map((entry) => [String(entry.rank), Number(entry.prize) || 0])), ...config.api_prizes?.[editBoard], [String(combined[idx].rank)]: Number(value) || 0 } } }));
    }
  };

  const addRow = () => {
    if (combined.length >= maxRows) return;
    const nextRank = combined.reduce((m, r) => Math.max(m, Number(r.rank) || 0), 0) + 1;
    writeCombined([...combined, { rank: nextRank, user: "new***usr", wagered: 0, prize: 0 }]);
  };

  const removeRow = (idx) => writeCombined(combined.filter((_, i) => i !== idx));

  const num = (v) => (v === "" ? "" : Number(v));

  const cleanBoard = (b) => ({
    ...b,
    settings: {
      prizePool: Number(b.settings.prizePool) || 0,
      affiliateCode: b.settings.affiliateCode || "",
    },
    topThree: b.topThree.map((t) => ({
      ...t,
      wagered: Number(t.wagered) || 0,
      prize: Number(t.prize) || 0,
    })),
    leaderboard: b.leaderboard.map((r) => ({
      ...r,
      rank: Number(r.rank) || 0,
      wagered: Number(r.wagered) || 0,
      prize: Number(r.prize) || 0,
    })),
  });

  const persistLocal = () => {
    const stakeClean = cleanBoard({
      settings: data.settings,
      topThree: data.topThree,
      leaderboard: data.leaderboard,
    });
    const exclusiveClean = cleanBoard(data.exclusive || emptyBoard());
    const clean = {
      ...data,
      settings: stakeClean.settings,
      topThree: stakeClean.topThree,
      leaderboard: stakeClean.leaderboard,
      exclusive: exclusiveClean,
    };
    saveState(clean);
    setData(clean);
  };

  // Save everything: local standings, sheet source, prize pool and YouTube channels.
  const saveAll = async () => {
    setSheetBusy(true);
    setYtBusy(true);
    try {
      persistLocal();
      const channels = ytChannels.map((c) => c.trim()).filter(Boolean);
      const [r1, r2] = await Promise.all([
        fetch(`${API}/admin/leaderboard-config`, {
          method: "PUT", headers: authHeaders(), body: JSON.stringify(sheetCfg),
        }),
        fetch(`${API}/admin/youtube-config`, {
          method: "PUT", headers: authHeaders(), body: JSON.stringify({ channels }),
        }),
      ]);
      const saved = await r1.json();
      if (!r1.ok) throw new Error(leaderboardFailure(saved));
      if (!r2.ok) throw new Error('YouTube settings could not be saved.');
      setSheetCfg(normalizeLeaderboardConfig(saved)); setSavedSource(saved.source);
      toast({ title: "All changes saved", description: "Leaderboard source, prize pool, standings and YouTube updated." });
      await Promise.all([refreshSheets(), refreshYt()]);
    } catch (failure) {
      setSheetError(failure.message);
      toast({ title: "Save failed", description: failure.message, variant: "destructive" });
    } finally {
      setSheetBusy(false);
      setYtBusy(false);
    }
  };

  const handleReset = () => {
    const fresh = resetState();
    setData(fresh);
    toast({ title: "Reset", description: "Restored default demo data." });
  };

  const logout = () => { authLogout(); navigate("/"); };

  return (
    <div className={`admin-panel admin-dashboard min-h-screen${navCollapsed ? ' is-collapsed' : ''}`} data-testid="admin-panel" data-users-view={activeSection.id === 'users'}>
      {/* Top bar */}
      <header className="admin-dashboard-header sticky top-0 z-40 bg-[#0a0b0e]/85 backdrop-blur-xl border-b border-emerald-500/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AdminSidebarToggle collapsed={navCollapsed} onCollapse={() => setNavCollapsed((value) => !value)} mobileOpen={mobileNav} onMobileChange={setMobileNav} />
            <Link to="/" data-testid="admin-logo-link" aria-label="GVRewards home" className="admin-brand-link"><img src={logo} alt="GVRewards" data-testid="admin-brand-logo" /></Link>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="admin-view-site" aria-label="View Site" title="View Site" className="text-slate-300 hover:text-white px-2 sm:px-4">
              <ArrowLeft className="sm:mr-2 h-4 w-4" /><span className="hidden sm:inline">View Site</span>
            </Button>
            <Button variant="ghost" onClick={logout} data-testid="admin-logout" aria-label="Logout" title="Logout" className="text-slate-300 hover:text-white px-2 sm:px-4">
              <LogOut className="sm:mr-2 h-4 w-4" /><span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <AdminNavigation logo={logo} active={activeSection.id} collapsed={navCollapsed} onCollapse={() => setNavCollapsed(!navCollapsed)} mobileOpen={mobileNav} onMobileChange={setMobileNav} />
      <main className="admin-workspace">
        {/* Header + actions */}
        <div className="admin-workspace-header">
          <div>
            <div className="admin-workspace-title-row"><h1 data-testid="admin-title" className="font-display font-bold text-white">{activeSection.title}</h1><div ref={setOriginalsActionsTarget} data-testid="admin-originals-heading-actions" /></div>
            <p data-testid="admin-subtitle" className="text-sm text-slate-400 mt-2">Admin Panel</p>
          </div>
          {['youtube', 'wager'].includes(activeSection.id) && <div className="admin-workspace-actions">
            <Button variant="outline" data-testid="admin-reset" onClick={handleReset} className="border-white/15 text-slate-200 hover:bg-white/5">
              <RotateCcw className="mr-2 h-4 w-4" /> Reset
            </Button>
            <Button onClick={saveAll} disabled={sheetBusy || ytBusy || !sheetReady} data-testid="save-all" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold disabled:opacity-60">
              <Save className="mr-2 h-4 w-4" /> Save Changes
            </Button>
          </div>}
        </div>

        <AdminSection
          title="YouTube"
          subtitle="Channels feeding the Latest Videos section"
          icon={Youtube}
          open={activeSection.id === 'youtube'}
          testid="group-youtube"
        >
          <SectionCard
            title="YouTube Channels"
            desc="Automatic refresh every 5 minutes · No API key required"
          >
            <div className="space-y-3" data-testid="youtube-editor">
              {ytChannels.map((ch, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-rose-400" />
                    <Input
                      data-testid={`youtube-url-${idx}`}
                      value={ch}
                      disabled={ytBusy}
                      onChange={(e) =>
                        setYtChannels((list) => list.map((c, i) => (i === idx ? e.target.value : c)))
                      }
                      placeholder="https://www.youtube.com/@YourChannel"
                      className="pl-9 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <button
                    onClick={() => setYtChannels((list) => (list.length > 1 ? list.filter((_, i) => i !== idx) : [""]))}
                    data-testid={`youtube-remove-${idx}`}
                    disabled={ytBusy}
                    className="flex items-center justify-center h-9 w-9 rounded-lg text-rose-400 hover:bg-rose-500/10"
                    aria-label="Remove channel"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() => setYtChannels((list) => [...list, ""])}
                data-testid="youtube-add"
                disabled={ytBusy || ytChannels.length >= 10}
                className="border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10"
              >
                <Plus className="mr-2 h-4 w-4" /> Add Channel
              </Button>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={saveYtCfg} disabled={ytBusy} data-testid="youtube-save" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold disabled:opacity-60">
                  <Save className="mr-2 h-4 w-4" /> Save Channels
                </Button>
                <Button variant="outline" onClick={refreshYt} disabled={ytBusy} data-testid="youtube-refresh" className="border-white/15 text-slate-200 hover:bg-white/5">
                  <RefreshCw className={`mr-2 h-4 w-4 ${ytBusy ? "animate-spin" : ""}`} /> Test / Refresh Now
                </Button>
              </div>
              <YouTubeFeedStatus status={ytStatus} error={ytError || ytFeedError} />
            </div>
          </SectionCard>
        </AdminSection>

        <AdminSection
          title="Wager Competition"
          subtitle="Leaderboard source, competition dates, prize pool and standings"
          icon={Trophy}
          open={activeSection.id === 'wager'}
          testid="group-wager"
        >
        <AdminLeaderboardSource config={sheetCfg} savedSource={savedSource} ready={sheetReady} onChange={(config) => { setSheetCfg(config); setSheetStatus(null); setSheetError(''); }} busy={sheetBusy} onSave={saveSheetCfg} onTest={testLeaderboardSource} onRefresh={refreshSheets} status={sheetStatus} statusKind={sheetStatusKind} error={sheetError} />

        <SectionCard title="Competition Settings" collapsible actions={<AdminBoardSelector value={editBoard} onChange={setEditBoard} />} desc="Headline prize pool for the selected board. This overrides the summed prize schedule for everyone.">
          <div className="max-w-sm space-y-1.5">
            <Label className="text-xs text-slate-400">Prize Pool ($) — {editBoard === "exclusive" ? "Stake Exclusive" : "Stake"}</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="prize-pool-input"
                type="number"
                value={sheetCfg[`${editBoard}_prize_pool`]}
                onChange={(e) =>
                  setSheetCfg((c) => ({ ...c, [`${editBoard}_prize_pool`]: e.target.value === "" ? 0 : Number(e.target.value) }))
                }
                className="bg-white/5 border-white/10 text-white flex-1"
              />
              <Button
                onClick={saveSheetCfg}
                disabled={sheetBusy || !sheetReady}
                size="sm"
                data-testid="prize-pool-save"
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold disabled:opacity-60 shrink-0 h-9 px-3"
              >
                <Save className="mr-1.5 h-4 w-4" /> Save
              </Button>
            </div>
            <p className="text-[11px] text-slate-500">Set to 0 to use the summed prize schedule.</p>
          </div>
        </SectionCard>

        <SectionCard
          title={`Standings — Top ${maxRows}`}
          desc={`One clean list of positions for ${editBoard === "exclusive" ? "Stake Exclusive" : "Stake"}. Position, Name, Wagered and Prize. Up to ${maxRows} players.`}
        >
          {/* column labels */}
          <div className="hidden sm:grid grid-cols-12 gap-2 px-1 pb-2 text-[10px] uppercase tracking-widest text-slate-500">
            <div className="col-span-2">Position</div>
            <div className="col-span-4">Name</div>
            <div className="col-span-2">Wagered ($)</div>
            <div className="col-span-3">Prize ($)</div>
            <div className="col-span-1" />
          </div>
          <div className="space-y-2" data-testid="standings-editor">
            {combined.map((r, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center rounded-xl bg-white/5 p-2.5">
                <Input value={r.rank} type="number"
                  disabled={sheetCfg.source === 'stake_api'}
                  data-testid={`standings-rank-${idx}`}
                  onChange={(e) => updatePlayer(idx, "rank", num(e.target.value))}
                  className="col-span-3 sm:col-span-2 bg-white/5 border-white/10 text-white" />
                <Input value={r.user}
                  disabled={sheetCfg.source === 'stake_api'}
                  data-testid={`standings-name-${idx}`}
                  onChange={(e) => updatePlayer(idx, "user", e.target.value)}
                  className="col-span-9 sm:col-span-4 bg-white/5 border-white/10 text-white" />
                <Input value={r.wagered} type="number"
                  disabled={sheetCfg.source === 'stake_api'}
                  data-testid={`standings-wagered-${idx}`}
                  onChange={(e) => updatePlayer(idx, "wagered", num(e.target.value))}
                  className="col-span-6 sm:col-span-2 bg-white/5 border-white/10 text-white" />
                <Input value={r.prize} type="number"
                  data-testid={`standings-prize-${idx}`}
                  onChange={(e) => updatePlayer(idx, "prize", num(e.target.value))}
                  className="col-span-5 sm:col-span-3 bg-white/5 border-white/10 text-white" />
                <button
                  onClick={() => removeRow(idx)}
                  data-testid={`standings-remove-${idx}`}
                  disabled={sheetCfg.source === 'stake_api'}
                  className="col-span-1 flex items-center justify-center h-9 rounded-lg text-rose-400 hover:bg-rose-500/10"
                  aria-label="Remove row"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              variant="outline"
              onClick={addRow}
              disabled={combined.length >= maxRows || sheetCfg.source === 'stake_api'}
              data-testid="standings-add"
              className="border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-40"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Player
            </Button>
            <span data-testid="admin-standings-count" className="text-xs text-slate-500">{combined.length} / {maxRows} players</span>
          </div>
        </SectionCard>
        </AdminSection>

        {activeSection.id === 'analytics' && <AnalyticsDashboard />}
        {activeSection.id === 'stake' && <AdminStakePanel />}
        {activeSection.id === 'kick' && <KickletAdminWorkspace />}
        {['originals', 'users', 'credits', 'alerts', 'audit', 'construction'].includes(activeSection.id) && <AdminToolsPanel section={activeSection.id} originalsContent={<AdminOriginalsGate key={user.id} active actionsTarget={originalsActionsTarget} />} usersContent={<section className="admin-users-workspace" data-testid="admin-users-workspace" aria-label="Registered users">
          <UsersToolbar search={userSearch} onSearch={setUserSearch} sort={userSort} onSort={setUserSort} count={visibleUsers.length} total={users.length} onRefresh={loadUsers} loading={usersLoading} />
          <UsersList users={visibleUsers} total={users.length} loading={usersLoading} viewerId={user.id} onDelete={setDeleteAccount} onSettings={(account) => { setManagedAccountTab('details'); setManagedAccount(account); }} />
        </section>} />}
      </main>
      {managedAccount && !deleteAccount && !balanceAccount && !accessAccount && !revealAccount && !controlAccount && <UserManagementDialog key={managedAccount.id} account={managedAccount} viewerId={user.id} initialTab={managedAccountTab} onTabChange={setManagedAccountTab} onReveal={setRevealAccount} onAccess={setAccessAccount} onControl={setControlAccount} onBalance={setBalanceAccount} onClose={() => setManagedAccount(null)} onUpdated={(details) => setUsers((current) => current.map((account) => account.id === details.id ? { ...account, ...details } : account))} />}
      {controlAccount && <UserControlDialog key={`${controlAccount.account.id}-${controlAccount.view}`} account={controlAccount.account} view={controlAccount.view} viewerId={user.id} onClose={() => setControlAccount(null)} onUpdated={(details) => setUsers((current) => current.map((account) => account.id === details.id ? { ...account, ...details } : account))} />}
      {deleteAccount && <DeleteUserDialog key={deleteAccount.id} account={deleteAccount} onClose={() => setDeleteAccount(null)} onDeleted={(userId) => { setUsers((current) => current.filter((account) => account.id !== userId)); setDeleteAccount(null); setManagedAccount(null); toast({ title: <span data-testid="admin-delete-success">Account permanently deleted</span>, description: 'Sign-in access and V-credit balances have been removed.' }); }} />}
      {balanceAccount && <BalanceDialog key={balanceAccount.id} account={balanceAccount} onClose={() => setBalanceAccount(null)} onSaved={(updated) => setUsers((current) => current.map((account) => account.id === updated.user_id ? { ...account, balance_minor: updated.balance_minor, vault_minor: updated.vault_minor ?? account.vault_minor, balance_revision: updated.revision } : account))} />}
      {accessAccount && <UserAccessDialog key={`${accessAccount.account.id}-${accessAccount.mode}`} account={accessAccount.account} initialMode={accessAccount.mode} onClose={() => setAccessAccount(null)} onSaved={(result) => { loadUsers(); toast({ title: result.password_changed ? 'Password reset' : 'Role updated', description: 'The user must sign in again.' }); }} />}
      {revealAccount && <PasswordRevealDialog key={revealAccount.id} account={revealAccount} onClose={() => setRevealAccount(null)} />}
    </div>
  );
};

export default Admin;
